import { useState, useCallback, type FC, useRef } from "react";
import AvatarEditor, { type AvatarEditorRef } from "react-avatar-editor";
import { type Accept, useDropzone } from "react-dropzone";
import "./index.scss";
import { Button } from "@components/button";
import { CropType, type FileUploaded } from "@archetypes/generic";
import { fileUpload, getFileSizeAsString } from "@utils/file-upload.util";
import { UploadMediaType } from "@archetypes/app";
import { checkFaviconResolution } from "@utils/file-upload.util";
interface ImageEditorProps {
  onFileUploaded: (fileUploaded: FileUploaded) => void;
  filePath?: string;
  fileName?: string;
  fileType: UploadMediaType;
  maxSize?: number;
  cropType?: CropType;
  onClose: () => void;
}

const getFileAcceptConfig = (fileType: UploadMediaType): Accept => {
  switch (fileType) {
    case UploadMediaType.FavIcon:
      return {
        "image/png": [],
        "image/ico": [],
        "image/svg-xl": [],
      };
    case UploadMediaType.Logo:
      return {
        "image/jpeg": [],
        "image/png": [],
        "image/webp": [],
        "image/jpg": [],
      };
  }
};

export const ImageEditor: FC<ImageEditorProps> = ({
  filePath,
  fileName,
  onFileUploaded,
  cropType,
  onClose,
  fileType,
  maxSize = 2097152,
}) => {
  const editorRef = useRef<AvatarEditorRef>(null);
  const [scaleValue, setScaleValue] = useState(1);
  const [selectedImage, setSelectedImage] = useState<File>();
  const [error, setError] = useState<string>();
  const [isFileUploading, toggleFileUploading] = useState<boolean>(false);

  const onSave = () => {
    const formData = new FormData();
    if (!selectedImage || !editorRef) {
      return;
    }

    editorRef?.current?.getImageScaledToCanvas().toBlob(async (file) => {
      if (!file) {
        return;
      }

      if (!filePath || !fileName) {
        return;
      }
      formData.append("filePath", filePath);
      formData.append("file", file, fileName);
      try {
        toggleFileUploading(true);
        const result = await fileUpload(filePath, file, fileName);
        if (result.length > 0) {
          onFileUploaded(result[0]);
          onClose();
        } else {
          throw new Error("Error Uploading file");
        }
      } catch (error) {
        setError("Error Uploading file");
      } finally {
        toggleFileUploading(false);
      }
    });
  };

  const onScaleChange = (scaleChangeEvent: React.ChangeEvent<HTMLInputElement>) => {
    const scaleValue = Number.parseFloat(scaleChangeEvent.target.value);
    setScaleValue(scaleValue);
  };

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      if (!file) return;

      if (fileType === UploadMediaType.FavIcon) {
        try {
          await checkFaviconResolution(file);
        } catch (err: unknown) {
          if (err instanceof Error) {
            setError(err.message);
          } else {
            setError("An unknown error occurred.");
          }
          return;
        }
      }

      // if (size > 2e6) {
      //   setError("Please upload image size of 2MB or less.");
      //   return;
      // }

      // const allowedTypes = fileType ? imageTypes[fileType] : defaultTypes;

      // if (!allowedTypes.includes(type)) {
      //   let typeMessage = "JPG, JPEG, SVG, ICO or PNG";
      //   if (fileType === UploadMediaType.FavIcon) {
      //     typeMessage = "SVG, ICO or PNG";
      //   } else if (fileType === UploadMediaType.Logo) {
      //     typeMessage = "PNG, JPG, JPEG, or WEBP";
      //   }
      //   setError(`Invalid file type. Only ${typeMessage} files are allowed.`);
      //   return;
      // }

      setSelectedImage(file);
    },
    [fileType],
  );

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    open: openFileDialog,
  } = useDropzone({
    onDrop,
    maxFiles: 1,
    maxSize,
    accept: getFileAcceptConfig(fileType),
  });

  const onChangePicture = useCallback(() => {
    setSelectedImage(undefined);
    openFileDialog();
  }, [openFileDialog]);

  return (
    <div className="image-cropper">
      {error ? <p>{error}</p> : null}
      {selectedImage ? (
        <>
          <div className="avatar-container">
            <AvatarEditor
              ref={editorRef}
              image={selectedImage}
              color={[0, 0, 0, 0.35]}
              border={10}
              width={250}
              height={250}
              scale={scaleValue}
              rotate={0}
              borderRadius={cropType === CropType.SQUARE ? 0 : 500}
            />
          </div>
          <input
            type="range"
            value={scaleValue}
            name="points"
            step={0.5}
            min="1"
            max="10"
            className="slider-input"
            onChange={onScaleChange}
          />
        </>
      ) : (
        <>
          <div {...getRootProps()} className="input">
            <input {...getInputProps()} />
            {isDragActive ? (
              <p>Drop the file here ...</p>
            ) : (
              <>
                <p>
                  Drag & Drop your image here, <br /> or click to select an image <br />
                  Upload Limit: {getFileSizeAsString(maxSize)}
                </p>
              </>
            )}
          </div>
        </>
      )}

      <div className="action">
        <Button
          variant="primary"
          onClick={onSave}
          loading={isFileUploading}
          loaderHeight={24}
          disabled={!selectedImage}
        >
          Save
        </Button>
        <Button onClick={onChangePicture} disabled={!selectedImage || isFileUploading}>
          Change Picture
        </Button>
      </div>
    </div>
  );
};
